import { create } from 'zustand'
import { Room, RoomEvent, Track, DisconnectReason, type RemoteParticipant, type LocalTrack, type TrackPublication, type RemoteTrackPublication } from 'livekit-client'
import { getVideoConfig, getIncomingLkQuality, nextBigRoomLatch, decodeBudget } from '../services/videoQuality'
import api from '../services/api'
import { getSocket } from '../services/socket'
import { mediaManager } from '../services/mediaManager'
import { runVoiceDiagnostic } from '../services/voiceDiagnostic'
import { startVoiceTelemetry, stopVoiceTelemetry } from '../services/voiceTelemetry'
import { installRemoteAudioWatchdog } from '../services/remoteAudioWatchdog'
import { ConnectionQualityWatcher } from '../services/connectionQualityWatcher'
import { emitVoiceActivity } from '../services/voiceActivity'
import { claimVoice } from '../services/voiceClaim'

// Single-room model — only one connected room at a time. Watchdog teardown
// closure lives at module scope so `disconnect()` can clear it without
// having to thread it through Zustand state.
let watchdogTeardown: (() => void) | null = null

// Per-connect bandwidth-suppression watcher. Same lifecycle as the
// watchdog above — created on Room connect, destroyed on disconnect.
// Lives at module scope so `toggleScreenShare` can read its
// `isSuppressed()` to refuse to start a share on a Poor connection.
let qualityWatcher: ConnectionQualityWatcher | null = null

// In-flight connect promise. Four call sites reach into this store's
// connect today (VoiceManager auto-reconnect, VoiceManager socket
// onConnect retry, useSocket voice-room rejoin, useVoiceFeatures
// channel switch). Each one does `if (isConnecting) return` first, but
// that's a check-then-set race: between reading `isConnecting === false`
// and the inner `set({ isConnecting: true })` running, two concurrent
// calls both see false and both proceed. The result is exactly what
// `dmCallStore` hit before its dedupe — two parallel Room objects
// sharing one LiveKit identity, the SFU kicks the older session with
// DUPLICATE_IDENTITY, peers see "connected" with no media. Mirror the
// dmCallStore dedupe here: a second concurrent connect for the SAME
// channel awaits the first's promise; a connect to a DIFFERENT channel
// takes the existing-room-teardown path inside the IIFE as before.
let connectPromise: Promise<void> | null = null
let connectingChannelId: string | null = null
// Auto-reconnect throttle (2026-08-06 reconnect storm): one broken client
// re-joined a room 171×/day in ~2s loops, and every rejoin churns
// subscriptions for the WHOLE room — the team read it as "everyone keeps
// disconnecting". All retry paths (socket on-connect, the OSPROD-288 rejoin
// watcher, the page-load ladder) funnel through connect(), so the gate lives
// here: auto attempts (silentIfBlocked) are counted in a sliding window, and
// once they get frequent each next attempt must wait exponentially longer.
// User-initiated joins are never throttled and clear the window.
// Kill-switch: localStorage voiceAutoRejoinBackoff='off'.
const AUTO_CONNECT_WINDOW_MS = 120_000
const AUTO_CONNECT_FREE_RETRIES = 3
const autoConnectTimes: number[] = []
function autoConnectRefusalMs(now: number): number {
  while (autoConnectTimes.length > 0 && now - autoConnectTimes[0] > AUTO_CONNECT_WINDOW_MS) autoConnectTimes.shift()
  if (autoConnectTimes.length < AUTO_CONNECT_FREE_RETRIES) return 0
  const requiredGap = Math.min(2_000 * 2 ** (autoConnectTimes.length - AUTO_CONNECT_FREE_RETRIES), 60_000)
  const last = autoConnectTimes[autoConnectTimes.length - 1]
  return Math.max(0, requiredGap - (now - last))
}
// OSPROD-287/288: the Room object as soon as it is constructed, BEFORE the
// store publishes it. `disconnect()` used to read `get().room`, which stays
// null until the very end of connect() — after the SFU handshake, audio
// unlock and setMicrophoneEnabled (which retries with backoff and can take
// seconds). A leave inside that window found no room and returned having torn
// down nothing, leaving a fully-connected orphan that still played soundboard
// clips and still held the mic. Tracking it here lets a leave reach the room
// no matter how early it arrives.
let pendingRoom: Room | null = null
import { useVoiceStore } from './voiceStore'
import { useAuthStore } from './authStore'
import { useToastStore } from './toastStore'
import {
  tryAcquireCallOwnership,
  releaseCallOwnership,
  requestCallTakeover,
  onCallTakeoverRequested,
} from '../services/callInstanceGuard'

interface LivekitState {
  room: Room | null
  isConnecting: boolean
  remoteParticipants: RemoteParticipant[]
  /**
   * Big-room auto cap (2026-06-12): true while the room has more camera
   * publishers than BIG_ROOM_VIDEO_ENGAGE — every remote camera is then
   * force-capped to the LOW layer (tiles are tiny in a 20+ grid; HIGH
   * layers are pure decode/bandwidth waste). Latched with a deadband,
   * see nextBigRoomLatch in services/videoQuality.
   */
  bigRoomForcedLow: boolean
  localAudioTrack: LocalTrack | undefined
  localVideoTrack: LocalTrack | undefined
  localScreenTrack: LocalTrack | undefined
  /** Ground-truth indicator: does the local audio track actually exist and is live? */
  isAudioTrackReady: boolean
  /** Ground-truth indicator: does the local video track actually exist and is live? */
  isVideoTrackReady: boolean
  /** Ground-truth indicator: does the local screen share track actually exist and is live? */
  isScreenTrackReady: boolean
  /**
   * Optimistic flags. Set immediately on user click and cleared (set
   * back to undefined) once the real LiveKit publish/unpublish settles.
   * Consumers prefer the optimistic value when defined so the UI flips
   * the instant the user clicks instead of waiting on getUserMedia /
   * SFU round-trips. If the real toggle resolves to a different value
   * (e.g. permission denied) the truth wins and the UI corrects itself.
   */
  optimisticMicEnabled: boolean | undefined
  optimisticVideoEnabled: boolean | undefined
  optimisticScreenEnabled: boolean | undefined
  /**
   * In-flight locks for the local-control toggles. While true the matching
   * toggle ignores further clicks — the second click would otherwise read
   * the optimistic state, fire a competing publish, and the two requests
   * race; the camera ends up flipping back to its previous value when the
   * later request resolves last (OSPROD-57).
   */
  micToggleInFlight: boolean
  videoToggleInFlight: boolean
  screenToggleInFlight: boolean
  /**
   * Set when a join was refused because ANOTHER browser tab already owns the
   * live call (single-instance guard, callInstanceGuard.ts). Holds the pending
   * join args so the "use here" button can take over + retry. null = not blocked.
   */
  blockedByOtherTab: {
    channelId: string
    username: string
    audioDeviceId?: string | null
    noiseSuppression?: boolean
  } | null
  connect: (
    channelId: string,
    username: string,
    audioDeviceId?: string | null,
    noiseSuppression?: boolean,
    options?: { silentIfBlocked?: boolean; takeover?: boolean },
  ) => Promise<void>
  disconnect: () => Promise<void>
  /** Dismiss the "call active in another tab" block without joining. */
  clearBlockedByOtherTab: () => void
  /** "Use here": take the call over from the other tab, then join. */
  takeoverCallHere: () => Promise<void>
  /** Move voice to THIS device from another device (explicit, after confirm). */
  takeoverVoiceHere: (channelId: string, username: string, audioDeviceId?: string | null, noiseSuppression?: boolean) => Promise<void>
  toggleMute: () => Promise<void>
  toggleVideo: () => Promise<void>
  toggleScreenShare: () => Promise<void>
  setAudioDevice: (deviceId: string | null) => Promise<void>
  setVideoDevice: (deviceId: string | null) => Promise<void>
  setVolume: (participantIdentity: string, volume: number) => void
}

// Recount camera publishers and (dis)engage the big-room LOW cap.
// Called on membership + publication churn; cheap (no awaits, walks the
// participant map once) and idempotent — only acts when the latch flips.
function applyBigRoomCap(room: Room) {
  let publishers = 0
  try {
    if (room.localParticipant.isCameraEnabled) publishers++
    for (const p of room.remoteParticipants.values()) {
      if (p.getTrackPublication(Track.Source.Camera)) publishers++
    }
  } catch {
    return // partial mocks / room mid-teardown
  }
  const engaged = useLivekitStore.getState().bigRoomForcedLow
  const next = nextBigRoomLatch(publishers, engaged)
  // Publisher-side cap for single-layer canvas (background) tracks runs on
  // EVERY event, not just latch flips: a background camera freshly published
  // into an already-busy room must be capped too. mediaManager no-ops unless
  // the LOCAL publish is a single-layer canvas track, so this is cheap.
  // (The subscribe-side sweep below only needs to run when the latch flips.)
  try { mediaManager.applyBusyRoomPublishCap(room, next) } catch { /* best-effort */ }
  if (next === engaged) return
  useLivekitStore.setState({ bigRoomForcedLow: next })
  console.info(`[livekit] big-room video cap ${next ? 'ENGAGED (forcing LOW)' : 'released'} — ${publishers} camera publishers`)
  // Engage: hard LOW ceiling on every remote camera. Release: back to the
  // user's preference ('auto' → HIGH ceiling; each VoiceTile's
  // setVideoDimensions retakes layer control on its next tick — the two
  // APIs are mutually exclusive, see the TrackSubscribed comment below).
  const incoming = useVoiceStore.getState().incomingQuality
  // Decode-budget ON + 'auto': the per-tile useTileVideoDimensions hook is the
  // layer authority (180p for every non-hero tile, size-based for the hero you
  // are looking at). A flat setVideoQuality sweep here would just fight it on
  // the next tick AND clobber the hero tile to 180p on every join/leave. Skip —
  // the hook reacts to the `bigRoomForcedLow` flip we just set above. Manual
  // MEDIUM/LOW users (hook inert) still get the flat cap below.
  if (decodeBudget() && incoming === 'auto') return
  const q = getIncomingLkQuality(next ? 'low' : incoming)
  for (const p of room.remoteParticipants.values()) {
    const pub = p.getTrackPublication(Track.Source.Camera) as RemoteTrackPublication | undefined
    if (!pub) continue
    try { pub.setVideoQuality(q) } catch { /* pub mid-teardown */ }
  }
}

export const useLivekitStore = create<LivekitState>((set, get) => ({
  room: null,
  isConnecting: false,
  remoteParticipants: [],
  bigRoomForcedLow: false,
  localAudioTrack: undefined,
  localVideoTrack: undefined,
  localScreenTrack: undefined,
  isAudioTrackReady: false,
  isVideoTrackReady: false,
  isScreenTrackReady: false,
  optimisticMicEnabled: undefined,
  optimisticVideoEnabled: undefined,
  optimisticScreenEnabled: undefined,
  micToggleInFlight: false,
  videoToggleInFlight: false,
  screenToggleInFlight: false,
  blockedByOtherTab: null,

  connect: async (channelId, username, audioDeviceId, noiseSuppression, options) => {
    // One-shot voice-feature diagnostic. Logs once per page-load and
    // is idempotent on subsequent calls. Lets support correlate
    // user-reported "no NS in browser X" with actual feature support
    // (AudioWorklet, WASM SIMD, sample rate, module workers) without
    // a 10-message back-and-forth.
    runVoiceDiagnostic()
    // (Auto-framing is no longer forced ON per join — mediaManager.setCameraEnabled
    // computes the effective state from the saved preference + background via
    // computeJoinState, so a user's saved-off sticks. Reversed the 2026-05-22 decision.)
    // Dedupe concurrent connects to the SAME channel (VoiceManager
    // auto-reconnect + socket on-connect retry can hit this within the
    // same tick). Hand the second caller the first's promise; a
    // connect to a DIFFERENT channel falls through to the existing-
    // room-teardown path inside the IIFE. See the comment at the
    // module-scope `connectPromise` declaration for the full story.
    if (connectPromise && connectingChannelId === channelId) {
      console.log('[livekit:connect] dedupe → awaiting in-flight connect for', channelId)
      return connectPromise
    }

    // Reconnect-storm gate — see autoConnectTimes at module scope. Refusing
    // is cheap: the rejoin watcher / next socket reconnect simply tries
    // again later, by which point the required gap may have elapsed.
    if (options?.silentIfBlocked) {
      const backoffOff = (() => { try { return localStorage.getItem('voiceAutoRejoinBackoff') === 'off' } catch { return false } })()
      if (!backoffOff) {
        const wait = autoConnectRefusalMs(Date.now())
        if (wait > 0) {
          console.warn(`[livekit:connect] auto-reconnect throttled (${autoConnectTimes.length} recent attempts) — retry allowed in ${Math.ceil(wait / 1000)}s`)
          return
        }
        autoConnectTimes.push(Date.now())
      }
    } else {
      // An explicit user join is a fresh start — don't hold storm history
      // against it, and let its outcome re-arm the auto ladder cleanly.
      autoConnectTimes.length = 0
    }

    connectingChannelId = channelId
    connectPromise = (async () => {
    const T0 = Date.now()
    const t = (label: string, extra?: Record<string, unknown>) => {
      // Mirrors `[dmCall:connect] +Nms` instrumentation so the voice
      // channel side gives the same level of detail when a "no audio
      // until refresh" report comes in. Keep verbose until the
      // join-flow races are firmly closed; then rip out.
      console.log(`[livekit:connect] +${Date.now() - T0}ms ${label}`, extra ?? '')
    }
    t('start', { channelId, username, audioDeviceId, noiseSuppression })
    // Single-instance guard: only ONE browser tab may own the live call
    // (callInstanceGuard.ts). Two tabs in a call = two DFN denoiser workers
    // contending for CPU on one machine → inference blows the 9ms budget →
    // degrade/flap, plus double-mic + echo (confirmed in prod: every user ran
    // ~2 concurrent call tabs). If another tab already owns the call, refuse to
    // start a second runtime here. Switching channels WITHIN this tab re-acquires
    // trivially (we already hold it). silentIfBlocked = auto-reconnect path: just
    // bail quietly; a user-initiated join shows the "use here" block modal.
    const owned = await tryAcquireCallOwnership()
    if (!owned) {
      t('blocked: live call owned by another tab')
      set({ isConnecting: false })
      if (!options?.silentIfBlocked) {
        set({ blockedByOtherTab: { channelId, username, audioDeviceId, noiseSuppression } })
      }
      return
    }
    // We own the call. If another tab later clicks "use here", leave so it can
    // take over (releases the lock as part of disconnect).
    onCallTakeoverRequested(() => { void get().disconnect() })
    set({ blockedByOtherTab: null })
    // Cross-device sticky ownership. The local Web Lock above only guards tabs
    // of THIS browser; a voice session held by the desktop app (or another
    // browser) is invisible to it. Ask the server. This MUST run before the
    // /livekit/token POST and room.connect() below: LiveKit identity is the bare
    // userId, so a non-owner that connects evicts the owner at the SFU — checking
    // after connect is too late (VOICE_CRITICAL_INVARIANTS: pre-connect gate).
    if (!options?.takeover) {
      const claim = await claimVoice(channelId)
      if (claim.blocked) {
        t('blocked: voice owned by another device', claim.activeOn)
        set({ isConnecting: false })
        // Automatic paths (silentIfBlocked) bail quietly AND fail closed — never
        // hijack. A user-initiated join surfaces the banner via voiceActiveElsewhere.
        useVoiceStore.getState().setVoiceActiveElsewhere(
          claim.activeOn ?? { channelId, deviceLabel: 'another device' },
        )
        releaseCallOwnership() // let go of the local lock we just took
        return
      }
    }
    // Reached here → we may proceed. Clear any prior "active elsewhere" flag.
    useVoiceStore.getState().setVoiceActiveElsewhere(null)
    // OSPROD-76 mutex teardown removed 2026-05-16. SharedMicCapture +
    // MediaContext (see services/mediaContext.ts) give voice channel
    // and DM call independent mic single-flight chains sharing one
    // getUserMedia + denoiser, so concurrent rooms no longer contest
    // tracks. Camera/screen stay mutex but bubble CameraBusyError to
    // the UI for a user-driven yield decision instead of silent
    // teardown of the other call.
    const existing = get().room
    if (existing) {
      t('existing voice room → disconnect', { state: (existing as any)?.state })
      mediaManager.stopHealthCheck()
      await existing.disconnect()
    }

    set({ isConnecting: true, isAudioTrackReady: false, isVideoTrackReady: false, isScreenTrackReady: false })
    useVoiceStore.getState().setBannedInfo(null)
    try {
      t('POST /livekit/token')
      const tokenRes = await api.post('/livekit/token', {
        roomName: channelId,
        participantName: username,
      }, { validateStatus: (s: number) => (s >= 200 && s < 300) || s === 403 })
      t('token resolved', {
        tokenStatus: (tokenRes as any)?.status,
        waiting: (tokenRes.data as any)?.waiting,
        hasToken: !!(tokenRes.data as any)?.token,
        url: (tokenRes.data as any)?.url,
      })
      // Waiting room: server returns 202 with `waiting:true` instead of a
      // token when the channel is locked and we don't have an APPROVED
      // entry. The waiting overlay is driven by `voice:waiting:waiting`
      // (already emitted by the server), so we just bail here without
      // attempting a LiveKit connect.
      if ((tokenRes as any).status === 202 || (tokenRes.data as any)?.waiting) {
        console.log('[livekit:connect] waiting-room sentinel, bailing without LiveKit join')
        set({ isConnecting: false })
        return
      }
      if ((tokenRes as any).status === 403 || (tokenRes.data as any)?.code === 'channel_banned') {
        const d = tokenRes.data as any
        useVoiceStore.getState().setBannedInfo({ channelId, reason: d?.reason ?? null, expiresAt: d?.expiresAt ?? null })
        console.log('[livekit:connect] channel ban, bailing without LiveKit join')
        set({ isConnecting: false })
        return
      }
      const { token, url } = tokenRes.data

      // Quality preset is applied per-camera-publish by mediaManager,
      // which reads voiceStore.videoQuality and passes explicit
      // captureOptions + publishOptions. The room-level defaults
      // below are just a safety net for any code path that calls
      // setCameraEnabled without going through mediaManager.
      const videoConfig = getVideoConfig(useVoiceStore.getState().videoQuality)
      // Screen share gets its own publish profile, intentionally NOT
      // tied to the user's videoQuality preset — when someone shares
      // their screen, every viewer needs to be able to read code,
      // documents, and small UI text. Past behaviour: screen share
      // inherited the camera preset (e.g. 250 kbps on 'low'), which
      // turned shared editors into mush. Subscribe-side quality caps
      // are also bypassed for screen share (see the TrackSubscribed
      // handler below).
      //
      // 6 Mbps / 30 fps → 3 Mbps / 15 fps (2026-08-26): a share is a
      // SINGLE layer with no simulcast rungs AND receive-cap exempt, so
      // its ceiling is what EVERY subscriber pays, with no way down. On
      // the 24-participant all-cameras call, N simultaneous shares made
      // each client's inbound jump 7–35 Mbps regardless of their
      // "Receive — downlink cap" setting. At a fixed resolution, bitrate
      // above ~3 Mbps mostly buys motion smoothness — 1080p text/IDE
      // sharing stays crisp; only full-motion video in a share degrades,
      // which is the right trade for a mixed-downlink fleet.
      const screenShareEncoding = { maxBitrate: 3_000_000, maxFramerate: 15 }
      const room = new Room({
        // adaptiveStream OFF: it picked simulcast layers based on the
        // rendered <video> size, which downgraded everyone to 540p when
        // tiles were small. We use a manual ResizeObserver on the
        // <video> element instead (see useTileVideoDimensions) so a
        // small grid tile still asks for L1, but a fullscreened tile
        // immediately jumps to the full 1080p layer.
        adaptiveStream: false,
        // dynacast ON: publisher pauses spatial layers nobody is
        // subscribed to. Saves CPU/uplink with no downside — when a
        // subscriber asks for HIGH the publisher resumes the high
        // layer transparently.
        dynacast: true,
        videoCaptureDefaults: { resolution: videoConfig.resolution },
        publishDefaults: {
          videoEncoding: videoConfig.encoding,
          // Camera codec: H.264 simulcast by default (2026-06-19) — see
          // videoQuality.cameraVideoCodec(). H.264 is HW-decoded on all Macs
          // (no Mac has HW VP9 decode) + Windows GPUs, so a big call's ×16-20
          // decode stays cheap. 'vp9' is opt-in via localStorage 'voiceVideoCodec'.
          videoCodec: videoConfig.codec,
          // scalabilityMode (SVC) is VP9/AV1-only; with H.264 LiveKit does
          // automatic 3-layer simulcast, so leave it undefined for H.264.
          scalabilityMode: videoConfig.codec === 'vp9' ? videoConfig.scalabilityMode : undefined,
          // backupCodec (a parallel H.264 simulcast for subscribers that can't
          // decode the primary — e.g. older Safari when primary is VP9) is only
          // needed when the PRIMARY is VP9. With H.264 primary it's redundant
          // (universally decodable) and would just double the publisher encode,
          // so omit it for H.264.
          backupCodec: videoConfig.codec === 'vp9' ? { codec: 'h264' as const } : undefined,
          // Screen share encoding is independent from camera. See the
          // screenShareEncoding comment above for why we override.
          screenShareEncoding,
          screenShareSimulcastLayers: [],
          // Audio-first degradation: under CPU / bandwidth pressure let the
          // video encoder shed quality (resolution + fps) instead of
          // fighting for bitrate. Combined with the lower default ceiling
          // (videoQuality 'auto' = 720p@1.2M, was 1080p@8M) this stops video
          // from over-probing a weak uplink into the BWE collapse that
          // starved audio (Sergey QA: inaudible with cam on at ~550 kbps).
          degradationPreference: 'balanced',
        },
      })
      // Reachable by disconnect() from this moment on — see pendingRoom.
      pendingRoom = room

      room.on(RoomEvent.ParticipantConnected, () => {
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
        applyBigRoomCap(room)
      })
      room.on(RoomEvent.ParticipantDisconnected, () => {
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
        applyBigRoomCap(room)
      })
      // Camera toggles change the publisher count without membership
      // churn — the big-room cap must follow those too.
      room.on(RoomEvent.TrackPublished, () => applyBigRoomCap(room))
      room.on(RoomEvent.TrackUnpublished, () => applyBigRoomCap(room))
      room.on(RoomEvent.LocalTrackPublished, () => applyBigRoomCap(room))
      room.on(RoomEvent.LocalTrackUnpublished, () => applyBigRoomCap(room))
      room.on(RoomEvent.TrackSubscribed, (track, publication) => {
        // Apply the user's downlink-side cap to every freshly-subscribed
        // remote video — but ONLY when they've manually picked
        // MEDIUM / LOW. On 'auto' we leave the cap unset so the
        // per-tile setVideoDimensions hook (useTileVideoDimensions)
        // can drive layer selection from the rendered tile size. The
        // two API paths are mutually exclusive in livekit-client
        // (setVideoDimensions clears requestedMaxQuality, see
        // RemoteTrackPublication.ts:176).
        //
        // Screen share is exempted — readability of shared code /
        // documents matters more than the receiver's bandwidth budget,
        // and the publisher already publishes a single high-bitrate
        // layer with no simulcast rungs to step down to. Capping at
        // MEDIUM/LOW would just blur text without saving bandwidth.
        if (publication.kind === Track.Kind.Video && publication.source !== Track.Source.ScreenShare) {
          const mode = useVoiceStore.getState().incomingQuality
          // Decode-budget ON + 'auto': defer to the per-tile hook (180p for
          // non-hero, size-based for the hero) instead of a flat LOW — same
          // reason as applyBigRoomCap. Flag OFF or manual mode keep the old
          // flat-cap behavior exactly.
          const hookOwnsLayer = decodeBudget() && mode === 'auto'
          if (get().bigRoomForcedLow && !hookOwnsLayer) {
            // Big-room cap beats everything: a freshly-subscribed camera
            // in a busy grid starts at LOW straight away.
            try {
              (publication as RemoteTrackPublication).setVideoQuality(getIncomingLkQuality('low'))
            } catch {}
          } else if (mode !== 'auto') {
            try {
              (publication as RemoteTrackPublication).setVideoQuality(getIncomingLkQuality(mode))
            } catch {}
          }
        }
        // Same bulletproof audio fallback the DM call surface uses
        // (see dmCallStore for the full rationale): LiveKit-client v2
        // doesn't auto-attach remote audio tracks to <audio> elements;
        // our LiveKitBackgroundAudio path goes through Web Audio,
        // which is gated on the shared AudioContext being unlocked.
        // If the gesture-unlock race loses, the Web Audio chain emits
        // nothing and `<media-playout>.totalSamplesCount` stays at 0.
        // An explicit `track.attach()` creates a hidden <audio> with
        // srcObject = MediaStream, which Chrome lets autoplay because
        // the source is a live WebRTC capture. Either path producing
        // sound is enough; with both wired, suspended-context bugs
        // can't silence the voice room.
        if (publication.kind === Track.Kind.Audio && track && (track as any).attach) {
          try {
            const el = (track as any).attach() as HTMLAudioElement
            el.style.display = 'none'
            // Silence the element so it doesn't double-up with the
            // Web Audio playback in LiveKitBackgroundAudio. The <audio>
            // is kept attached as a decoder keep-alive in case the
            // shared AudioContext is stuck suspended, but it never
            // produces audible output by itself.
            //
            // Why `.volume = 0` and NOT `.muted = true`: Chromium's
            // WebRTC AEC tracks unmuted HTMLMediaElement playback as
            // part of its echo-cancellation reference signal. Setting
            // `.muted = true` removes the element from that reference
            // path; our actual audible playback is through Web Audio
            // (AudioContext.destination), which Chromium does NOT
            // include in the WebRTC AEC reference. End result: AEC
            // had nothing to subtract against → wild echo during big
            // calls (reported on v0.1.178A). `.volume = 0` keeps the
            // element "playing" silently so AEC still sees it as a
            // valid reference source.
            //
            // We can safely use `.volume = 0` (instead of the stronger
            // `.muted = true` defense) because every prior call site
            // that used to set this element's volume back to
            // peerVolume — VoiceManager peerVolumes subscriber, both
            // deafen/un-deafen branches of authStore, the screen-share
            // useEffect in VoiceChannelView — was removed in v0.1.177A.
            // The store-exported setVolume wrappers clamp to [0,1] so
            // even a future regression can't push this above 0.
            el.volume = 0
            document.body.appendChild(el)
          } catch (err) {
            console.warn('[livekit:room] track.attach failed:', err)
          }
        }
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
      })
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        // Tear down any <audio> elements track.attach() built above.
        // Without this a long voice session with churning participants
        // accumulates orphan hidden <audio> nodes.
        if (track && (track as any).detach) {
          try {
            const els = (track as any).detach() as HTMLMediaElement[] | HTMLMediaElement
            const arr: HTMLMediaElement[] = Array.isArray(els) ? els : els ? [els] : []
            arr.forEach((el) => {
              try { el.remove() } catch { /* noop */ }
            })
          } catch { /* noop */ }
        }
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
      })
      // OSPROD-203: refresh the participant list on mic mute/unmute. With
      // keep-publication-alive mute (OSPROD-146) a mute/unmute toggles the
      // track's `muted` state WITHOUT a publish/subscribe event, so without
      // these handlers `remoteParticipants` never changes reference and the
      // grid never re-derives `isMuted` (from `isMicrophoneEnabled`) — the
      // muted-mic icon stays on a participant who has unmuted and is audible
      // (and vice-versa). Fires for the LOCAL participant too, so the self tile
      // updates as well. Unlike ActiveSpeakersChanged below, mute toggles are
      // user-driven and infrequent → no re-render/flicker concern.
      room.on(RoomEvent.TrackMuted, () => {
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
      })
      room.on(RoomEvent.TrackUnmuted, () => {
        set({ remoteParticipants: Array.from(room.remoteParticipants.values()) })
      })
      // Note: ActiveSpeakersChanged intentionally omitted — it does not change
      // the participant list, only audio levels. Updating remoteParticipants here
      // causes unnecessary re-renders and video flicker.
      room.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
        console.error('[LiveKit] TrackSubscriptionFailed:', trackSid, participant.identity)
      })
      // Generic data-channel hub for in-room ephemeral signals
      // (raise-hand, soundboard, etc.). Decode JSON and dispatch by type;
      // unknown types are ignored so future additions don't break older
      // clients that haven't shipped yet.
      room.on(RoomEvent.DataReceived, (payload, participant) => {
        // OSPROD-287: every branch below is an IN-CALL effect (a sound, a
        // reaction, someone's raised hand). None of them may fire for a user
        // who is no longer in this channel. The listener dies with the room,
        // so reaching here after a leave means the room outlived the leave —
        // which happens when disconnect() lands while connect() is still in
        // flight, or when a queued reconnect retry wins the race. Those are
        // fixed below, but the gate stays: it makes the symptom impossible
        // regardless of why a room lingers. Reported as "I left the voice
        // channel and still hear its soundboard".
        if (useVoiceStore.getState().currentChannelId !== channelId) return
        try {
          const text = new TextDecoder().decode(payload)
          const msg = JSON.parse(text)
          if (msg?.type === 'raise-hand' && typeof msg.raised === 'boolean') {
            const id = msg.identity || participant?.identity
            if (id) useVoiceStore.getState().setRaisedHand(id, msg.raised)
          } else if (msg?.type === 'away' && typeof msg.away === 'boolean') {
            // OSPROD-61: "Be right back" flag broadcast — same path as
            // raise-hand; remotes flip the hourglass overlay on the
            // sender's tile.
            const id = msg.identity || participant?.identity
            if (id) useVoiceStore.getState().setAway(id, msg.away)
          } else if (msg?.type === 'reaction' && typeof msg.emoji === 'string') {
            // Floating emoji burst from another participant. Identity is
            // self-reported but we fall back to the SDK-provided
            // sender — same defence-in-depth as the raise-hand path.
            const id = msg.identity || participant?.identity
            if (id) useVoiceStore.getState().pushReaction(id, msg.emoji)
          } else if (msg?.type === 'sound-play' && typeof msg.fileUrl === 'string') {
            // Soundboard: receivers play the clip locally — the file is
            // fetched from /uploads/sounds/<name>, no audio is mixed
            // into the mic track. Async-imported to avoid a circular
            // dep (soundboard.ts → livekitStore → here).
            console.debug('[Soundboard] sound-play received', {
              fileUrl: msg.fileUrl,
              name: msg.name,
              from: participant?.identity,
            })
            import('../services/soundboard').then(({ playSoundLocal }) => {
              playSoundLocal(msg.fileUrl)
            }).catch((err) => console.warn('[Soundboard] dynamic import failed', err))
          }
        } catch {
          // ignore malformed payloads from older / foreign senders
        }
      })
      room.on(RoomEvent.LocalTrackPublished, (pub: TrackPublication) => {
        if (pub.track) {
          if (pub.kind === Track.Kind.Audio && pub.source === Track.Source.Microphone) {
            set({ localAudioTrack: pub.track as LocalTrack, isAudioTrackReady: true })
          } else if (pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera) {
            set({ localVideoTrack: pub.track as LocalTrack, isVideoTrackReady: true })
          } else if (pub.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare) {
            set({ localScreenTrack: pub.track as LocalTrack, isScreenTrackReady: true })
            // OSPROD-142: drive server VoiceState.isScreenShare off the
            // actual track lifecycle so the LIVE badge can't desync — the
            // in-app toggle also emits, this keeps it authoritative.
            const cid = useVoiceStore.getState().currentChannelId
            if (cid) {
              try { getSocket().emit('voice:state:update', { channelId: cid, isScreenShare: true }) } catch { /* noop */ }
            }
          }
        }
      })
      room.on(RoomEvent.LocalTrackUnpublished, (pub: TrackPublication) => {
        if (pub.source === Track.Source.Microphone) {
          set({ localAudioTrack: undefined, isAudioTrackReady: false })
        }
        if (pub.source === Track.Source.Camera) {
          set({ localVideoTrack: undefined, isVideoTrackReady: false })
        }
        if (pub.source === Track.Source.ScreenShare) {
          set({ localScreenTrack: undefined, isScreenTrackReady: false })
          // OSPROD-142: the browser's native "Stop sharing" bar ends the
          // track WITHOUT running toggleScreenShare, so the server stayed
          // isScreenShare:true and the LIVE badge stuck for everyone.
          // Clear it off the unpublish event — covers native-stop AND the
          // in-app button (idempotent).
          const cid = useVoiceStore.getState().currentChannelId
          if (cid) {
            try { getSocket().emit('voice:state:update', { channelId: cid, isScreenShare: false }) } catch { /* noop */ }
          }
        }
      })

      // Mirror dmCallStore's lifecycle listeners. Without these,
      // a server-side kick / ICE timeout / signal failure left the
      // store thinking the room was up and the user only saw "no
      // audio" with zero console clues. Verbose for now — easy to
      // grep when QA hits the "had to refresh to hear audio" repro.
      room.on(RoomEvent.Disconnected, (reason) => {
        console.warn('[livekit:room] Disconnected', { reason, sid: (room as any)?.sid })
        // OSPROD-288. This used to log and nothing else, so the store kept a
        // DEAD Room forever: every "am I still in a call?" check reads
        // `room` and a dead object is still truthy, which disabled the
        // page-load reconnect ladder, the roster reconciler and the mic
        // banner alike. Prod telemetry showed the cost — 17 sessions across
        // 13 users in 7 days kept sampling with ice_state
        // disconnected/failed/closed for an average of TWO HOURS (worst: 7h).
        // That is the ticket's "периодически отключает, приходится
        // перезаходить": media was gone, the UI still said connected, and
        // nothing but a manual rejoin could fix it. By the time this event
        // fires LiveKit has already exhausted its own reconnect attempts, so
        // the honest move is to publish the truth and let VoiceManager's
        // rejoin watcher take over. CLIENT_INITIATED is our own disconnect()
        // — skip it to avoid re-entrancy.
        if (reason === DisconnectReason.CLIENT_INITIATED) return
        const intended = useVoiceStore.getState().currentChannelId === channelId
        void get().disconnect().catch(() => {})
        if (!intended) return
        try {
          useToastStore.getState().showToast({
            id: `voice-dropped-${channelId}-${Date.now()}`,
            type: 'error',
            title: 'Voice disconnected',
            body: 'Connection lost — reconnecting…',
          })
        } catch { /* toast best-effort */ }
      })
      room.on(RoomEvent.Reconnecting, () => {
        console.warn('[livekit:room] Reconnecting')
      })
      room.on(RoomEvent.Reconnected, () => {
        console.info('[livekit:room] Reconnected')
        // A reconnect is a discrete, legitimate reason to grant the health-
        // check mic/camera recovery a FRESH strike budget. Without this, a
        // desync that already spent the 3-strike budget BEFORE the reconnect
        // stays given-up forever and the mic is stranded in 'idle' until a full
        // page refresh (2026-06-05 big-call incident: a reconnect storm left
        // ~5 web users unable to unmute for 25-45 min). The health-check drives
        // off the persisted mute INTENT, so once re-armed it re-asserts the mic
        // on its next 1 s tick even when the immediate re-apply below is skipped
        // (isMicrophoneEnabled=false post-reconnect) or transiently fails. Must
        // run BEFORE the isMicrophoneEnabled guard. See invariants #20 + #63.
        mediaManager.resetRecoveryBudget()
        // After a LiveKit reconnect, the SFU may have re-published
        // our raw audio without our denoiser graph in the loop —
        // peers would hear unprocessed mic until the next health-
        // check window noticed (5-60s of bad audio depending on
        // which probe fired). Force a re-apply through mediaManager
        // so the denoiser cascade is reasserted immediately. Only
        // when the user's intent IS mic-on (don't fight a
        // deliberately-muted user back into broadcasting).
        if (room.localParticipant.isMicrophoneEnabled) {
          mediaManager.setMicrophoneEnabled(room, true).catch((err) => {
            console.warn('[livekit:room] Re-apply mic after Reconnected failed:', err)
          })
        }
      })
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        console.log('[livekit:room] ConnectionStateChanged', { state })
      })
      room.on(RoomEvent.MediaDevicesError, (err) => {
        console.error('[livekit:room] MediaDevicesError', err)
      })
      room.on(RoomEvent.SignalConnected, () => {
        console.log('[livekit:room] SignalConnected')
      })

      // Connect with default ICE (host → srflx → relay, whatever ICE picks).
      // TURN servers come from LiveKit's join response (embedded TURN, verified
      // working 2026-10-15) and ICE escalates to relay on its own when the
      // direct path fails. NOTE: livekit-client reads ICE config (iceServers /
      // iceTransportPolicy) ONLY from room.connect's 3rd arg `{ rtcConfig }` —
      // RoomOptions passed to `new Room` has no such field and drops it
      // silently (that's how the June mobile relayFallback was a no-op).
      // Setting rtcConfig.iceServers here would also REPLACE LiveKit's TURN list.
      // The "forceRelay" (iceTransportPolicy:'relay') opt-in was removed
      // 2026-05-21 (relay-only made users inaudible). Defensively clear any
      // stale localStorage flag left from it.
      try { localStorage.removeItem('forceRelay') } catch { /* ignore */ }
      t('room.connect.begin', { url })
      await room.connect(url, token)
      t('room.connect.done', {
        sid: (room as any)?.sid,
        localIdentity: room.localParticipant?.identity,
        remoteCount: room.remoteParticipants.size,
      })

      // Unlock audio playback (required by Chrome autoplay policy)
      await mediaManager.unlockAudio(room)
      t('mediaManager.unlockAudio.done')

      const micOptions: { deviceId?: string; noiseSuppression?: boolean } = {}
      if (audioDeviceId) micOptions.deviceId = audioDeviceId
      if (typeof noiseSuppression === 'boolean') micOptions.noiseSuppression = noiseSuppression

      const isMuted = useAuthStore.getState().user?.settings?.isMuted ?? false
      const isDeafened = useAuthStore.getState().user?.settings?.isDeafened ?? false
      const shouldEnableMic = !isMuted && !isDeafened

      t('setMicrophoneEnabled.begin', { shouldEnableMic, micOptions })
      const actualAudio = await mediaManager.setMicrophoneEnabled(room, shouldEnableMic, micOptions)
      t('setMicrophoneEnabled.done', { actualAudio, micEnabledOnRoom: room.localParticipant.isMicrophoneEnabled })

      set({
        room,
        isConnecting: false,
        isAudioTrackReady: actualAudio,
        remoteParticipants: Array.from(room.remoteParticipants.values()),
      })
      t('state-set, fully connected', {
        remoteCount: room.remoteParticipants.size,
        remoteIdentities: Array.from(room.remoteParticipants.values()).map((p) => p.identity),
      })

      // OSPROD-92: camera intent restore. Mic is restored above from
      // `user.settings.isMuted`; camera lacked an equivalent persisted
      // intent, so reconnect / page-refresh / channel-rejoin always
      // came back audio-only even when the user had video on before.
      // `lastCameraEnabled` mirrors the mic pattern via localStorage
      // (written by toggleVideo). Fire-and-forget after the room state
      // is published so the UI flips to "connected" immediately —
      // peer publication of our camera lands ~200-500ms later.
      // videoToggleInFlight + optimisticVideoEnabled are set so a fast
      // user click during the restore can't race a second publish.
      const shouldEnableCamera = useVoiceStore.getState().lastCameraEnabled
      if (shouldEnableCamera) {
        t('camera-restore.begin')
        set({ videoToggleInFlight: true, optimisticVideoEnabled: true })
        mediaManager.setCameraEnabled(room, true)
          .then((actualVideo) => {
            t('camera-restore.done', { actualVideo })
            set({ isVideoTrackReady: actualVideo, optimisticVideoEnabled: undefined })
            if (actualVideo) {
              try {
                getSocket().emit('voice:state:update', { channelId, isVideoOn: true })
              } catch {}
            }
          })
          .catch((err) => {
            console.warn('[livekit:connect] camera restore failed:', err)
            set({ optimisticVideoEnabled: undefined })
          })
          .finally(() => {
            set({ videoToggleInFlight: false })
          })
      }

      // Auto-recover any remote mic track whose RTP packets stop flowing
      // despite still being "subscribed" — replicates a browser refresh
      // for ONE track instead of disconnecting the whole room.
      if (watchdogTeardown) { try { watchdogTeardown() } catch {} }
      watchdogTeardown = installRemoteAudioWatchdog(room)

      // Auto-suppress video/screen on Poor / Lost local connection
      // quality so the audio stream always has the bandwidth budget.
      // The watcher also broadcasts the state via participant
      // metadata so peers can show "Vasya's connection is slow" in
      // his tile. See services/connectionQualityWatcher.ts.
      if (qualityWatcher) { try { qualityWatcher.destroy() } catch {} }
      qualityWatcher = new ConnectionQualityWatcher(room, {
        onLocalSuppress: (reason) => {
          useToastStore.getState().showToast({
            id: `bandwidth-suppress-${Date.now()}`,
            type: 'error',
            title: reason === 'lost' ? 'Connection lost' : 'Слабый интернет',
            body: reason === 'lost'
              ? 'Видео и экран отключены — пытаемся восстановить связь, голос вернётся первым.'
              : 'Видео и экран отключены, чтобы освободить канал под голос. Камера сама включится, когда сеть восстановится.',
          })
        },
        onLocalRestore: () => {
          useToastStore.getState().showToast({
            id: `bandwidth-restore-${Date.now()}`,
            type: 'message',
            title: 'Связь восстановилась',
            body: 'Камера включена обратно. Экран нужно расшарить заново вручную.',
          })
        },
      })

      // Start periodic health check to catch desync between settings and runtime
      mediaManager.startHealthCheck(() => {
        const r = get().room
        const user = useAuthStore.getState().user
        // OSPROD-92: drive expectedVideoOn from the persisted intent, NOT
        // from `r.localParticipant.isCameraEnabled`. The latter reads the
        // CURRENT state, so a missing camera (initial restore failed,
        // LiveKit reconnect dropped the publish, OS held the device) would
        // look "consistent with being off" and the health check would
        // never recover it. Reading the intent makes the 5s reconciler
        // a safety net for the camera the same way it already is for mic.
        return {
          room: r,
          expectedMuted: user?.settings?.isMuted ?? false,
          expectedVideoOn: useVoiceStore.getState().lastCameraEnabled,
          expectedScreenShare: r?.localParticipant.isScreenShareEnabled ?? false,
        }
      })

      // TEMP voice-diag telemetry (gated by VOICE_DIAG_ENABLED). Best-effort:
      // the collector first polls the server kill-switch and no-ops when off,
      // and every internal step is wrapped so it can never affect the call.
      const diagRoom = get().room
      if (diagRoom) {
        startVoiceTelemetry(diagRoom, { roomKind: 'channel', channelId, userName: username }).catch(() => {})
      }
    } catch (err: any) {
      // Single dump for every failure mode so a "no audio until refresh"
      // bug report comes back with enough context to triage without a
      // second repro pass.
      console.error('[livekit:connect] FAILED', {
        name: err?.name,
        message: err?.message,
        code: err?.code,
        status: err?.response?.status,
        responseData: err?.response?.data,
        stack: err?.stack,
        elapsedMs: Date.now() - T0,
      })
      set({ isConnecting: false, isAudioTrackReady: false, isVideoTrackReady: false, isScreenTrackReady: false })
      throw err
    }
    })().finally(() => {
      // Clear the in-flight markers AFTER the IIFE settles so the next
      // connect (room switch, retry after disconnect) takes the fresh
      // path instead of resolving to the previous attempt's outcome.
      connectPromise = null
      connectingChannelId = null
    })
    return connectPromise
  },

  disconnect: async () => {
    if (watchdogTeardown) {
      try { watchdogTeardown() } catch {}
      watchdogTeardown = null
    }
    if (qualityWatcher) {
      try { qualityWatcher.destroy() } catch {}
      qualityWatcher = null
    }
    mediaManager.cleanup()
    // Fall back to the in-flight room: `get().room` is only populated at the
    // very end of connect(), so a leave that arrives mid-connect would
    // otherwise tear down nothing and orphan a live room (OSPROD-287).
    const room = get().room ?? pendingRoom
    if (room) {
      // Release the per-room MediaContext FIRST so the shared mic
      // capture's refCount drops and (if no other room is using the
      // mic) the hardware capture gets torn down, AND the camera /
      // screen mutex ownership clears so a different call can claim
      // them immediately. The legacy setMicrophoneEnabled(false)
      // calls below remain as a belt-and-braces guarantee that the
      // LiveKit-level flags settle to false before the room closes.
      try { await mediaManager.forRoom(room).destroy() } catch (e) { console.warn('disconnect mediaContext destroy:', e) }
      // Explicitly stop all local tracks before disconnecting to ensure
      // microphone/camera LEDs turn off and peers stop receiving audio.
      try { await room.localParticipant.setMicrophoneEnabled(false) } catch (e) { console.warn('disconnect mic off:', e) }
      try { await room.localParticipant.setCameraEnabled(false) } catch (e) { console.warn('disconnect cam off:', e) }
      try { await room.localParticipant.setScreenShareEnabled(false) } catch (e) { console.warn('disconnect screen off:', e) }
      // Unpublish any manually-published fallback tracks
      room.localParticipant.trackPublications.forEach((pub) => {
        if (pub.track) {
          try { pub.track.stop() } catch {}
        }
      })
      await room.disconnect()
    }
    pendingRoom = null
    // TEMP voice-diag: stop the telemetry collector(s) + final flush.
    try { stopVoiceTelemetry() } catch { /* noop */ }
    // Single-instance guard: leaving the call → release ownership so another
    // tab can claim it (also fires when this tab yields to a "use here" takeover).
    try { releaseCallOwnership() } catch { /* noop */ }
    set({
      room: null,
      remoteParticipants: [],
      bigRoomForcedLow: false,
      localAudioTrack: undefined,
      localVideoTrack: undefined,
      localScreenTrack: undefined,
      isAudioTrackReady: false,
      isVideoTrackReady: false,
      isScreenTrackReady: false,
      optimisticMicEnabled: undefined,
      optimisticVideoEnabled: undefined,
      optimisticScreenEnabled: undefined,
      micToggleInFlight: false,
      videoToggleInFlight: false,
      screenToggleInFlight: false,
    })
  },

  clearBlockedByOtherTab: () => set({ blockedByOtherTab: null }),

  takeoverCallHere: async () => {
    const pending = get().blockedByOtherTab
    if (!pending) return
    // Ask the owning tab to leave + grab the lock (callInstanceGuard polls for
    // release, steals as a last resort). Then retry the join we were blocked on.
    set({ blockedByOtherTab: null })
    const ok = await requestCallTakeover()
    if (!ok) {
      useToastStore.getState().showToast({
        id: 'call-takeover-failed',
        type: 'error',
        title: 'Не удалось перехватить звонок',
        body: 'Закройте звонок в другой вкладке и попробуйте снова',
      })
      return
    }
    await get().connect(
      pending.channelId,
      pending.username,
      pending.audioDeviceId,
      pending.noiseSuppression,
    )
  },

  takeoverVoiceHere: async (channelId, username, audioDeviceId, noiseSuppression) => {
    useVoiceStore.getState().setVoiceActiveElsewhere(null)
    await get().connect(channelId, username, audioDeviceId, noiseSuppression, { takeover: true })
  },

  toggleMute: async () => {
    const { room, micToggleInFlight } = get()
    // Drop concurrent clicks — the second click would optimistically flip
    // again and fire a racing publish; whichever resolves last wins
    // (OSPROD-57: rapid double-clicks made the camera/mic flicker on/off).
    if (!room || micToggleInFlight) return
    const currentEnabled = get().optimisticMicEnabled ?? room.localParticipant.isMicrophoneEnabled
    const nextEnabled = !currentEnabled
    set({ micToggleInFlight: true, optimisticMicEnabled: nextEnabled, isAudioTrackReady: nextEnabled })
    try {
      // Pass the explicit target instead of mediaManager.toggleMute, which
      // re-reads `isMicrophoneEnabled` at call-time and could compute the
      // wrong target if a previous request hasn't finished propagating yet.
      const actualEnabled = await mediaManager.setMicrophoneEnabled(room, nextEnabled)
      set({ optimisticMicEnabled: undefined, isAudioTrackReady: actualEnabled })
    } catch (err) {
      console.error('[livekit] toggleMute failed, reverting:', err)
      // OSPROD-248: record the underlying reason so the "Microphone toggle
      // failed" toast is queryable from voice_diag (rawStats mic-toggle-fail).
      mediaManager.noteMicToggleFailure('livekit-store', err)
      useToastStore.getState().showToast({
        id: `mic-toggle-${Date.now()}`,
        type: 'error',
        title: 'Microphone toggle failed',
        body: 'Please try again.',
      })
      set({ optimisticMicEnabled: undefined, isAudioTrackReady: room.localParticipant.isMicrophoneEnabled })
    } finally {
      set({ micToggleInFlight: false })
    }
  },

  toggleVideo: async () => {
    const { room, videoToggleInFlight } = get()
    if (!room || videoToggleInFlight) return
    const currentEnabled = get().optimisticVideoEnabled ?? room.localParticipant.isCameraEnabled
    const nextEnabled = !currentEnabled
    set({ videoToggleInFlight: true, optimisticVideoEnabled: nextEnabled, isVideoTrackReady: nextEnabled })
    try {
      const actualEnabled = await mediaManager.setCameraEnabled(room, nextEnabled)
      set({ optimisticVideoEnabled: undefined, isVideoTrackReady: actualEnabled })
      // OSPROD-92: persist the user's camera intent so reconnect /
      // page-refresh paths can restore it. Use the ACTUAL result, not
      // the requested target — if setCameraEnabled fell back to false
      // (permissions, busy device) we shouldn't keep the user's intent
      // stuck on "on" and silently retry forever.
      useVoiceStore.getState().setLastCameraEnabled(actualEnabled)
      const cid = useVoiceStore.getState().currentChannelId
      if (cid) {
        getSocket().emit('voice:state:update', { channelId: cid, isVideoOn: actualEnabled })
      }
    } catch (err) {
      console.error('[livekit] toggleVideo failed, reverting:', err)
      // CameraBusyError is the deliberate mutex signal from
      // mediaManager — another call (likely the DM call) already
      // owns the camera. Render a friendly explainer instead of the
      // generic "Camera toggle failed" so the user knows where to
      // free the resource.
      const isBusyErr = (err as any)?.name === 'CameraBusyError'
      // OSPROD-271: both bounded enable attempts timed out (stuck pre-publish
      // await — seen while a call recording was active). Distinct copy so the
      // user knows the camera never started rather than a generic failure;
      // the stage is already in voice_diag {type:'camera-enable-stall'}.
      const isTimeoutErr = (err as any)?.name === 'CameraEnableTimeoutError'
      useToastStore.getState().showToast({
        id: `cam-toggle-${Date.now()}`,
        type: 'error',
        title: isBusyErr ? 'Camera is in use' : isTimeoutErr ? 'Camera didn\'t start' : 'Camera toggle failed',
        body: isBusyErr
          ? `"${(err as any).ownerLabel}" is using the camera. Turn it off there first.`
          : isTimeoutErr
            ? 'The camera took too long to initialize. Please try again.'
            : 'Please try again.',
      })
      set({ optimisticVideoEnabled: undefined, isVideoTrackReady: room.localParticipant.isCameraEnabled })
    } finally {
      set({ videoToggleInFlight: false })
    }
  },

  toggleScreenShare: async () => {
    const { room, screenToggleInFlight } = get()
    if (!room || screenToggleInFlight) return
    const currentEnabled = get().optimisticScreenEnabled ?? room.localParticipant.isScreenShareEnabled
    const nextEnabled = !currentEnabled
    // Bandwidth gate. Screen-share at typical resolutions needs
    // ≥ 1 Mbps to be intelligible; on a Poor/Lost connection it
    // would just starve audio while peers see a frozen still. Block
    // the start; allow the stop unconditionally so the user is
    // never trapped sharing on a dead connection.
    if (nextEnabled && qualityWatcher?.isSuppressed()) {
      useToastStore.getState().showToast({
        id: `screen-bandwidth-block-${Date.now()}`,
        type: 'error',
        title: 'Слишком слабая сеть для шаринга',
        body: 'Чтобы расшарить экран, нужно стабильное соединение (≥ 1 Mbps). Сейчас связь слабая — голос приоритетнее.',
      })
      return
    }
    set({ screenToggleInFlight: true, optimisticScreenEnabled: nextEnabled, isScreenTrackReady: nextEnabled })
    try {
      const actualEnabled = await mediaManager.setScreenShareEnabled(room, nextEnabled, { audio: true })
      set({ optimisticScreenEnabled: undefined, isScreenTrackReady: actualEnabled })
      const cid = useVoiceStore.getState().currentChannelId
      if (cid) {
        getSocket().emit('voice:state:update', { channelId: cid, isScreenShare: actualEnabled })
      }
    } catch (err) {
      console.error('[livekit] toggleScreenShare failed, reverting:', err)
      const isBusyErr = (err as any)?.name === 'CameraBusyError'
      useToastStore.getState().showToast({
        id: `screen-toggle-${Date.now()}`,
        type: 'error',
        title: isBusyErr ? 'Screen share is in use' : 'Screen share toggle failed',
        body: isBusyErr
          ? `"${(err as any).ownerLabel}" is sharing its screen. Stop it there first.`
          : 'Please try again.',
      })
      set({ optimisticScreenEnabled: undefined, isScreenTrackReady: room.localParticipant.isScreenShareEnabled })
    } finally {
      set({ screenToggleInFlight: false })
    }
  },

  setAudioDevice: async (deviceId) => {
    const { room, micToggleInFlight } = get()
    if (!room) return
    // setAudioDevice republishes the mic track under the hood. If the
    // user clicks through the device dropdown rapidly, two overlapping
    // republish calls fight for the same publication and Chrome may
    // hand out a silent or wrong-device track. Reuse the existing
    // mic in-flight flag so the picker and the mute button share one
    // lock (the picker is rendered next to the mute button — they
    // already coordinate UX-wise, just not implementation-wise).
    if (micToggleInFlight) return
    set({ micToggleInFlight: true })
    try {
      const actualEnabled = await mediaManager.setAudioDevice(room, deviceId)
      set({ isAudioTrackReady: actualEnabled })
      emitVoiceActivity(useVoiceStore.getState().currentChannelId, 'device_mic')
    } finally {
      set({ micToggleInFlight: false })
    }
  },

  setVideoDevice: async (deviceId) => {
    const { room, videoToggleInFlight } = get()
    if (!room) return
    // Mirror setAudioDevice: switching the camera republishes the video
    // track under the hood. Reuse the camera in-flight lock so the device
    // picker and the camera toggle button can't republish over each other
    // (rapid picks → wrong-device or black track). (OSPROD-101)
    if (videoToggleInFlight) return
    set({ videoToggleInFlight: true })
    try {
      const actualEnabled = await mediaManager.setVideoDevice(room, deviceId)
      set({ isVideoTrackReady: actualEnabled })
      emitVoiceActivity(useVoiceStore.getState().currentChannelId, 'device_camera')
    } finally {
      set({ videoToggleInFlight: false })
    }
  },

  setVolume: (participantIdentity, volume) => {
    const { room } = get()
    if (!room) return
    const participant = room.remoteParticipants.get(participantIdentity)
    if (!participant) return
    // livekit-client's setVolume sets `el.volume = v` on every attached
    // HTMLMediaElement — and HTMLMediaElement.volume is clamped to
    // [0,1]: anything above throws IndexSizeError. The peer-volume
    // slider goes up to ~2× (boost), but boost is the Web Audio
    // GainNode's job (see LiveKitBackgroundAudio). Clamp here so
    // legacy callers can't crash the call. Boost > 1× is silently
    // capped at 1 — boost callers should use the gain node directly.
    participant.setVolume(Math.max(0, Math.min(1, volume)))
  },
}))
